import { TableCell, TableRow } from "~/components/ui/table";
import { useNavigate } from "@tanstack/react-router";
import StatusBadge from "./StatusBadge";
import type { ListApplication } from "~/types/application.types";
import { formatDate } from "~/utils/date-formatter";
import { getRoleDisplayName } from "~/utils/role-formatter";
import type { CollaboratorRole } from "~/types/application.types";
import { Button } from "../ui/button";
import { Info, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "~/lib/orpc-client";
import { QUERY_KEYS } from "~/constants/queryKeys";
import { useState } from "react";
import { Spinner } from "../ui/spinner";
import { useTenantPath } from "~/hooks/use-tenant-path";
import { formatCategory } from "~/utils/format-category";

type Application = ListApplication;

type ApplicationRowProps = {
  application: Application;
  isCollaboratedView?: boolean;
  showOwner?: boolean;
};

export default function ApplicationRow({
  application,
  isCollaboratedView = false,
  showOwner = false,
}: ApplicationRowProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const tenantPath = useTenantPath();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const dashboardTitle =
    application.category === "WRITER" ? "" : application.title || "-";

  const { mutate: deleteApplication, isPending: isDeletingApplication } =
    useMutation({
      mutationFn: async () => {
        return apiClient.applications.delete({
          id: application.id,
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.APPLICATIONS_LIST,
        });
        setIsModalOpen(false);
        navigate({ to: tenantPath("/dashboard") });
      },
      onError: (error: Error) => {
        console.error(
          error.message || "Failed to delete application. Please try again."
        );
        setIsModalOpen(false);
      },
    });

  const handleRowClick = () => {
    // Only allow editing if the user owns the application and it's in progress
    const canEdit =
      !isCollaboratedView &&
      application.isOwner &&
      application.applicationStatus === "IN_PROGRESS";

    if (canEdit) {
      navigate({
        to: tenantPath("/apply/$applicationId") ,
        params: { applicationId: application.id.toString() },
      });
    } else {
      // View-only mode
      navigate({ to: tenantPath(`/applications/${application.id}`) });
    }
  };

  return (
    <TableRow
      className="hover:bg-primary/10 transition-colors cursor-pointer group"
      onClick={handleRowClick}
    >
      <TableCell className="font-bold text-foreground py-5 px-6 truncate max-w-72">
        <span>{dashboardTitle}</span>
      </TableCell>
      {showOwner && (
        <TableCell className="py-5 px-6 text-foreground">
          {application.isOwner ? (
            <span className="font-medium">You</span>
          ) : (
            <span>{application.ownerName || "-"}</span>
          )}
        </TableCell>
      )}
      <TableCell className="py-5 px-6">
        <span className="font-semibold text-foreground">
          {formatCategory(application)}
        </span>
      </TableCell>
      {isCollaboratedView && (
        <TableCell className="py-5 px-6">
          <span className="text-foreground">
            {(application as Application & { collaboratorRole?: string })
              .collaboratorRole
              ? getRoleDisplayName(
                  (application as Application & { collaboratorRole?: string })
                    .collaboratorRole as CollaboratorRole
                )
              : "-"}
          </span>
        </TableCell>
      )}
      <TableCell className="py-5 px-6 font-medium">
        {formatDate(application.updatedAt)}
      </TableCell>
      <TableCell className="py-5 px-6">
        <StatusBadge status={application.applicationStatus} />
      </TableCell>
      <TableCell className="px-6" onClick={(e) => e.stopPropagation()}>
        {(() => {
          const canDelete =
            application.isOwner &&
            application.applicationStatus === "IN_PROGRESS";
          const canShowHowToUse = !!application.certificationNumber;

          if (canShowHowToUse) {
            return (
              <Button
                variant="outline"
                size="sm"
                aria-label="How to use certificate"
                onClick={() =>
                  navigate({
                    to: tenantPath(
                      `/apply/how-to-use/${application.certificationNumber}`
                    ),
                  })
                }
                className="gap-1.5"
              >
                <Info className="w-4 h-4" />
                How to use
              </Button>
            );
          }

          if (!canDelete) {
            return <span className="px-3 text-foreground">-</span>;
          }

          return (
            <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
            <DialogTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                aria-label="Delete application"
                title="Delete"
                className="hover:text-red-500 hover:bg-transparent focus:ring-none"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Delete Application</DialogTitle>
                <DialogDescription>
                  Are you sure you want to delete the application to certify
                  {dashboardTitle ? (
                    <>
                      {" "}<span className="font-semibold text-foreground">{dashboardTitle}</span>
                      {application.authorName && (
                        <> by <span className="font-semibold text-foreground">{application.authorName}</span></>
                      )}
                    </>
                  ) : null}
                  ? This action cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DialogClose>
                <Button
                  variant="destructive"
                  onClick={() => deleteApplication()}
                  disabled={isDeletingApplication}
                >
                  {isDeletingApplication ? (
                    <Spinner className="size-4" />
                  ) : (
                    "Delete"
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          );
        })()}
      </TableCell>
    </TableRow>
  );
}
